import * as React from 'react';
import { decideMassageTypeSelectKey } from './massageTypeSelectKeyDecision';

export interface MassageTypeOption {
    type: string;
    defaultPrice: number;
}

interface MassageTypeSelectProps {
    options: MassageTypeOption[];
    value: string;
    onChange: (type: string) => void;
    onSelect?: (option: MassageTypeOption) => void;
    disabled?: boolean;
    autoFocus?: boolean;
    placeholder?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    className?: string;
    /**
     * Contract: called only for keydown events MassageTypeSelect does not
     * fully handle itself.
     *
     * NOT forwarded (handled internally):
     * - ArrowUp / ArrowDown: moves the highlighted option.
     * - Enter while the dropdown is open with a highlighted option: selects
     *   that option and closes the dropdown. It does NOT submit.
     *
     * Forwarded to the caller:
     * - Enter when there is no highlighted option to select (list closed,
     *   or the typed text does not match any option). This is the free-text
     *   path for a brand-new service name that is not in the dropdown, and
     *   it must keep working - the caller's handler is expected to submit
     *   using the current typed value.
     * - Enter pressed a second time right after a selection (the dropdown
     *   is now closed, so nothing is "highlighted open") - this is how a
     *   caller implements "select, then Enter again to submit".
     * - Escape, after the dropdown has been closed locally.
     */
    onKeyDown?: (e: React.KeyboardEvent) => void;
}

export const MassageTypeSelect: React.FC<MassageTypeSelectProps> = ({
    options,
    value,
    onChange,
    onSelect,
    disabled,
    autoFocus,
    placeholder = 'Type to search...',
    inputRef,
    className = 'form-control form-control-sm',
    onKeyDown,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [highlightIndex, setHighlightIndex] = React.useState(-1);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);
    const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    // Filter options to partial matches
    const filteredOptions = React.useMemo(() => {
        if (!value.trim()) return options;
        const term = value.toLowerCase();
        return options.filter(o => o.type.toLowerCase().includes(term));
    }, [options, value]);

    // Reset highlight when filtered list changes
    React.useEffect(() => {
        if (filteredOptions.length > 0) {
            setHighlightIndex(0);
        } else {
            setHighlightIndex(-1);
        }
    }, [filteredOptions]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option: MassageTypeOption) => {
        onChange(option.type);
        onSelect?.(option);
        setIsOpen(false);
        setHighlightIndex(-1);
    };

    // The decision of what to do and whether to forward is pure and unit
    // tested in massageTypeSelectKeyDecision.ts; this handler is just the
    // thin orchestrator that applies it to React/DOM state.
    const handleKeyDown = (e: React.KeyboardEvent) => {
        const decision = decideMassageTypeSelectKey({
            key: e.key,
            isOpen,
            highlightIndex,
            optionCount: filteredOptions.length,
        });

        if (decision.preventDefault) e.preventDefault();

        switch (decision.action.kind) {
            case 'open-and-highlight-first':
                setIsOpen(true);
                setHighlightIndex(0);
                break;
            case 'navigate': {
                const next = decision.action.direction === 'down'
                    ? Math.min(highlightIndex + 1, filteredOptions.length - 1)
                    : Math.max(highlightIndex - 1, 0);
                setHighlightIndex(next);
                itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
                break;
            }
            case 'select':
                handleSelect(filteredOptions[decision.action.index]);
                break;
            case 'close-dropdown':
                setIsOpen(false);
                break;
            case 'none':
                break;
        }

        if (decision.forward) onKeyDown?.(e);
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <input
                ref={inputRef}
                type="text"
                className={className}
                value={value}
                onChange={e => { onChange(e.target.value); setIsOpen(true); }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                autoFocus={autoFocus}
                placeholder={placeholder}
            />
            {isOpen && filteredOptions.length > 0 && (
                <div ref={listRef} style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1050,
                    background: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 4px 6px rgba(0,0,0,.1)',
                }}>
                    {filteredOptions.map((option, i) => (
                        <div
                            key={option.type}
                            ref={el => { itemRefs.current[i] = el; }}
                            style={{
                                padding: '6px 10px',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '13px',
                                background: i === highlightIndex ? '#e9ecef' : undefined,
                            }}
                            onMouseEnter={() => setHighlightIndex(i)}
                            onMouseDown={e => { e.preventDefault(); handleSelect(option); }}
                        >
                            <span>{option.type}</span>
                            <span style={{ color: '#6c757d', fontSize: '12px' }}>
                                {option.defaultPrice.toLocaleString()} THB
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
